/**
 * PlansV2 — nouvelle page Tarifs publique d'Archer (route `/plans`).
 *
 * Transposition React de la référence design validée
 * (export Claude Design « Archer Tarifs », décodé).
 *
 * Conventions de transposition appliquées (identiques à HomeV2) :
 *   - styles inline de la référence          → objets `style` React
 *   - `style-hover="…"`                      → classes `.pv2-*` (PlansV2.css)
 *   - `sc-camel-view-box`                    → `viewBox`
 *   - `sc-camel-on-click="{{ h }}"`          → `onClick={h}`
 *   - `<sc-if value="{{ x }}">`              → rendu conditionnel
 *   - `<sc-for list="{{ l }}" as="a">`       → `.map()`
 *   - le script `text/x-dc` (FAQS / MATRIX / PLANS + `renderVals`)
 *     → constantes + état React ci-dessous
 *
 * La copy est contractuelle : textes et montants viennent de la référence
 * mot pour mot. Aucun chiffre n'a été ajouté ni arrondi.
 *
 * La nav et le footer du gabarit de la référence sont ignorés : la page
 * monte dans le chrome v2 partagé (SiteHeaderV2 + SiteFooterV2), extrait
 * de la home et commun aux pages migrées.
 *
 * Sections, dans l'ordre de la référence :
 *   Hero · 4 cartes · bouton « Voir toutes les fonctionnalités » + matrice
 *   · blocs avocat / lettre 19€ · CTA final · FAQ
 *
 * Encaissement : le câblage Stripe est repris À L'IDENTIQUE de
 * `PricingPlansPage` — POST /api/subscriptions/checkout {tier}, redirection
 * vers `checkout_url`, 401 → /signup?next=/plans&tier=…, 503 structuré →
 * QuotaModal. Les identifiants de tier (starter/pro) sont inchangés.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SiteHeaderV2, { SITE_HEADER_HEIGHT } from '../components/siteV2/SiteHeaderV2';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import PageHead from '../components/seo/PageHead';
import QuotaModal, { extractCheckoutNotConfigured } from '../components/QuotaModal';
import ConsentExecutionCheckbox from '../components/ConsentExecutionCheckbox';
import PaymentConfirmModal from '../components/PaymentConfirmModal';
import PromoCodeField, { formatCents } from '../components/PromoCodeField';
import { useAuth } from '../contexts/AuthContext';
import {
  PRICE_EUR,
  PRICE_ANNUAL_EUR,
  OVERAGE_EUR,
  HIGHLIGHT_TIER,
  annualPerMonthEur,
  annualSavingEur,
  formatEurCompact,
  normalizeBillingCycle,
  normalizeTier,
} from '../constants/plans';
import { getPageMetadata } from '../lib/seo/metadata';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
import { usePlansT } from '../hooks/usePlansT';
import { useToast } from '../hooks/use-toast';
import { track } from '../utils/analytics';
import { takeSignupPromo } from '../utils/signupPromo';
import useStartRoute from '../hooks/useStartRoute';
import './PlansV2.css';
import '../styles/hero.css';

/* Retour d'un paiement ABANDONNÉ chez Stripe. Le cancel_url renvoie ici
   avec `?subscription=cancel` ; personne ne lisait ce paramètre, donc
   quelqu'un qui venait de renoncer retombait sur la grille tarifaire
   sans un mot — et sans savoir si sa carte avait été touchée. Elle ne
   l'a pas été : c'est la seule chose à lui dire.
   Trois chaînes, ici plutôt que dans le catalogue, comme GUIDES_HEADING
   dans Home/Footer.jsx. */
const CANCEL_NOTICE = {
  fr: "Paiement annulé. Rien n'a été débité.",
  nl: 'Betaling geannuleerd. Er is niets afgeschreven.',
  en: 'Payment cancelled. Nothing was charged.',
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mascotte — même résolution tolérante que HomeV2 : si l'asset n'est pas
// commité, `require` throw et on masque simplement l'image du CTA final.
const assetUrl = (m) => (m && m.default) || m || null;
let MASCOT_SRC = null;
try {
  // eslint-disable-next-line global-require
  MASCOT_SRC = assetUrl(require('../assets/mascotte-archer.png'));
} catch (_) {
  MASCOT_SRC = null;
}
let MASCOT_WEBP = null;
try {
  // eslint-disable-next-line global-require
  MASCOT_WEBP = assetUrl(require('../assets/mascotte-archer-520.webp'));
} catch (_) {
  MASCOT_WEBP = null;
}
// Mascotte du hero — le hibou au bouclier, distinct de celle du CTA final.
// Même résolution tolérante : sans l'asset, le hero rend simplement sans image.
let HERO_MASCOT_SRC = null;
try {
  // eslint-disable-next-line global-require
  HERO_MASCOT_SRC = assetUrl(require('../assets/mascotte-bouclier.png'));
} catch (_) {
  HERO_MASCOT_SRC = null;
}
// Dimensions intrinsèques du fichier : posées sur la balise pour que le
// navigateur réserve la boîte avant décodage (aucun layout shift). La
// largeur d'affichage, elle, est en CSS — c'est elle qui varie au responsive.
const HERO_MASCOT_W = 528;
const HERO_MASCOT_H = 697;

// ─────────────────────────────────────────────────────────────────────
// Icônes (SVG de la référence, `sc-camel-view-box` → `viewBox`)
// ─────────────────────────────────────────────────────────────────────
const Check = ({ size = 10, stroke = '#16a34a' }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke}
    strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Chevron = ({ size = 14, stroke = '#9ca3af', width = 2.4, style }) => (
  <svg
    style={style} width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Bolt = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#fbbf24" stroke="none" aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────
// Données de la référence (script `text/x-dc`)
// ─────────────────────────────────────────────────────────────────────
//
// Les LIBELLÉS — cartes, matrice, FAQ — vivent dans i18n/plans.json. Ne
// restent ici que ce qui ne se traduit pas : les NOMS DE PLAN
// (FREE/STARTER/PRO, des noms propres), les identifiants de tier attendus
// par le backend, et l'habillage. Les MONTANTS viennent tous de
// `constants/plans` — aucun chiffre n'est retapé dans ce fichier.

// Colonnes de la matrice / entêtes de l'accordéon mobile. Trois colonnes
// depuis la grille v2 : la colonne ULTIMATE a disparu avec le palier.
// Le prix affiché en tête de colonne est le MENSUEL sans engagement —
// le prix de référence, celui qu'on paie si on ne s'engage pas.
const MATRIX_PLANS = [
  { name: 'FREE', price: formatEurCompact(PRICE_EUR.free), color: '#15803d', idx: 0 },
  { name: 'STARTER', price: formatEurCompact(PRICE_EUR.starter), color: '#2563eb', idx: 1 },
  { name: 'PRO', price: formatEurCompact(PRICE_EUR.pro), color: '#1a56db', idx: 2 },
];

// Teintes des 4 groupes de la matrice.
const GHUES = [
  { bg: '#eff6ff', fg: '#1a56db' },
  { bg: '#fffbeb', fg: '#b45309' },
  { bg: '#faf5ff', fg: '#7c3aed' },
  { bg: '#f4f4f1', fg: '#6b7280' },
];

// Les 4 cartes. `tier` porte l'identifiant backend (inchangé) et sert
// aussi de clé dans i18n/plans.json (`cards.<tier>`) ; `kind` distingue le
// CTA gratuit (navigation) des CTA payants (checkout Stripe).
//
// Il n'y a plus de puce à début gras suivi d'une queue normale : la
// référence écrit la puce du dossier juriste d'un seul trait, sans
// rupture typographique. `emphasis` et `featureTail` ont disparu avec
// elle — un mécanisme de découpage par langue pour un seul cas qui n'est
// plus mis en forme ne se garde pas « au cas où ».
//
// Les MONTANTS ne sont plus écrits ici : ils viennent de
// `constants/plans.js`, le catalogue que lisent déjà l'inscription et les
// Paramètres. Deux surfaces qui vendent la même formule à deux prix
// différents, c'est un litige, pas un bug d'affichage.
const CARDS = [
  {
    tier: 'free',
    kind: 'link',
    name: 'FREE',
    bar: '#16a34a',
    nameColor: '#15803d',
    price: formatEurCompact(PRICE_EUR.free),
    suffix: null,
  },
  {
    tier: 'starter',
    kind: 'subscribe',
    // Le badge est passé de Pro à Starter. C'est là que se joue le vrai
    // pas — de « je sais » à « quelqu'un se bat pour moi » — et c'est
    // celui-là qu'on veut rendre évident. Le mettre sur Pro poussait vers
    // le plan cher un client qui n'a qu'un seul litige.
    highlight: HIGHLIGHT_TIER === 'starter',
    name: 'STARTER',
    bar: '#60a5fa',
    nameColor: '#2563eb',
    price: formatEurCompact(PRICE_EUR.starter),
    suffix: '/mois',
  },
  {
    tier: 'pro',
    kind: 'subscribe',
    highlight: HIGHLIGHT_TIER === 'pro',
    name: 'PRO',
    bar: '#0a0a0f',
    nameColor: '#0a0a0f',
    price: formatEurCompact(PRICE_EUR.pro),
    suffix: '/mois',
  },
];

// Montants par cycle, lus du catalogue. `perMonth` est le chiffre mis en
// avant sur la carte en cycle annuel (34€ / 69€) : le gros chiffre est
// TOUJOURS un prix mensuel, sinon les deux cycles ne se comparent plus.
// `annual` est le montant réellement débité en une fois — il ne s'écrit
// plus sur la carte mais à l'étape de confirmation, juste avant de payer.
// `savingPct` et `savingEur` alimentent la pastille d'économie de la
// carte annuelle : DÉRIVÉS des deux prix, jamais saisis — un pourcentage
// qui se calcule ne peut pas rester faux après un changement de grille.
// Aligné sur backend/constants/billing.py.
const CYCLE_PRICING = Object.fromEntries(
  ['starter', 'pro'].map((tier) => [tier, {
    annual:    formatEurCompact(PRICE_ANNUAL_EUR[tier]),
    perMonth:  formatEurCompact(annualPerMonthEur(tier)),
    savingPct: Math.round((1 - annualPerMonthEur(tier) / PRICE_EUR[tier]) * 100),
    savingEur: Math.round(annualSavingEur(tier)),
  }]),
);

// Les montants des ACTES, injectés dans la ligne sous les cartes et dans
// la FAQ. Écrits une fois ici, jamais dans le dictionnaire : un prix qui
// vit dans trois traductions dérive dans deux d'entre elles.
const OVERAGE_TOKENS = {
  '{ANALYSIS}':   formatEurCompact(OVERAGE_EUR.analysis),
  '{JURISTCASE}': formatEurCompact(OVERAGE_EUR.juristCase),
  '{REGISTERED}': formatEurCompact(OVERAGE_EUR.registeredMail),
  '{ONESHOT}':    formatEurCompact(OVERAGE_EUR.letterOneshot),
};

/** Remplace les jetons de montant dans une chaîne traduite. */
function withPrices(text) {
  return Object.entries(OVERAGE_TOKENS).reduce(
    (acc, [token, value]) => acc.split(token).join(value),
    String(text || ''),
  );
}

// Le suffixe de prix (« /mois ») est le seul morceau de la carte qui
// dépende de la langue tout en vivant à côté du montant.
const PRICE_SUFFIX = { fr: '/mois', nl: '/maand', en: '/month' };

// Grille de la matrice : libellé large + 4 colonnes de plans.
const MATRIX_COLS = {
  display: 'grid',
  gridTemplateColumns: '2.4fr 1fr 1fr 1fr',
  alignItems: 'stretch',
};

// La colonne MISE EN AVANT dans la matrice suit le badge des cartes.
// Deux mises en avant différentes sur le même écran — le badge sur une
// carte, la teinte sur une autre colonne — se lisent comme deux
// recommandations contradictoires.
const HIGHLIGHT_COL = Math.max(
  0, MATRIX_PLANS.findIndex((p) => p.name.toLowerCase() === HIGHLIGHT_TIER),
);

// Style d'une cellule de la matrice (`cell()` de la référence).
//
// « ✓ » et « — » restent comparés en dur : ce sont des GLYPHES, pas des
// mots — ils sont les mêmes dans les trois langues, et c'est justement ce
// qui permet à ce test de survivre à la traduction. La branche « Bientôt »
// de la référence a disparu : cette valeur est désormais portée par un
// drapeau `soon`, pas par la chaîne affichée, qui elle se traduit.
function cellStyle(v, highlighted) {
  if (v === '✓') {
    return {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 20, height: 20, borderRadius: '50%', fontSize: 11, fontWeight: 700,
      ...(highlighted
        ? { background: '#1a56db', color: '#fff', boxShadow: '0 2px 8px rgba(26,86,219,.3)' }
        : { background: '#dcfce7', color: '#16a34a' }),
    };
  }
  if (v === '·') return { fontSize: 12.5, fontWeight: 500, color: '#6b7280' };
  return {
    fontSize: 12.5, fontWeight: 600, textAlign: 'center',
    ...(highlighted
      ? { color: '#1a56db', background: '#eff6ff', borderRadius: 8, padding: '3px 10px' }
      : { color: '#374151' }),
  };
}

// Une coche de la matrice se rend en `<span>` ; le glyphe « ✓ » de la
// référence est conservé tel quel dans les données.
const HIGHLIGHT_COL_BG = 'rgba(26,86,219,.05)';

export default function PlansV2() {
  // `pickPlansLang()` lisait `archer_locale` une fois, sans jamais se
  // remettre à jour : la page ne bougeait pas quand le sélecteur changeait
  // de langue. Le store partagé, lui, re-rend.
  const [lang] = useSiteLanguage();
  const t = usePlansT(lang);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();

  // `?tier=pro&cycle=annual` — la formule et le cycle CHOISIS AILLEURS.
  // C'est par là que revient un client qui vient de s'inscrire en ayant
  // coché Pro/Annuel dans le formulaire, et par là aussi qu'il repart
  // quand le checkout renvoie 401 (cf. `handleSubscribe`). Sans ce
  // passage de témoin, le choix fait à l'inscription se perdait entre
  // deux pages et le client se retrouvait devant la grille, à recommencer.
  const urlTier = searchParams.get('tier');
  const urlCycle = searchParams.get('cycle');

  const [busy, setBusy] = useState(null);
  // Formule en attente de confirmation. « Choisir » ne part plus vers
  // Stripe : il ouvre l'étape de confirmation, où vit désormais la case
  // de rétractation (art. VI.53 CDE). La vitrine reste une vitrine ; le
  // consentement se donne au moment de payer.
  const [confirmTier, setConfirmTier] = useState(null);
  // Cycle de facturation. ANNUEL par défaut à l'écran — c'est
  // `DEFAULT_BILLING_CYCLE` qui tranche, pour cette page comme pour
  // l'inscription : le prix d'entrée annoncé partout est « dès 34 €/mois »
  // et l'écran d'arrivée doit le montrer. Rien n'est engagé pour autant :
  // l'étape de confirmation affiche le montant réellement débité et
  // l'engagement de douze mois avant tout paiement, et le backend, lui,
  // retombe sur le MENSUEL si le champ manque (jamais un engagement par
  // défaut).
  //
  // Un `?cycle=` valide dans l'URL est le choix déjà fait ailleurs (il
  // vient de /signup) ; une valeur inconnue retombe sur le défaut.
  const [cycle, setCycle] = useState(() => normalizeBillingCycle(urlCycle));
  // Lettre à l'unité (19 €) — la carte ouvre un écran qui dit ce que le
  // produit contient AVANT le clic qui paie, avec la case de
  // rétractation. Elle renvoyait jusqu'ici vers /signup sans rien vendre.
  const [oneshotOpen, setOneshotOpen] = useState(false);
  const [oneshotConsent, setOneshotConsent] = useState(false);
  const [oneshotBusy, setOneshotBusy] = useState(false);
  const [oneshotError, setOneshotError] = useState(null);
  // Codes promo. Le code n'est PAS un état de la page : c'est ce que
  // l'écran de confirmation a fait valider par le serveur, et qu'il faut
  // renvoyer au checkout. Deux états séparés parce que ce sont deux
  // offres distinctes — un code valable pour l'une ne l'est pas
  // forcément pour l'autre, et c'est le serveur qui tranche.
  const [subPromo, setSubPromo] = useState(null);
  const [oneshotPromo, setOneshotPromo] = useState(null);
  // Code hérité du formulaire d'inscription (sessionStorage, pas l'URL —
  // cf. utils/signupPromo.js). Lu UNE fois au montage et effacé dans la
  // foulée : sans ça, un client qui annule, va voir une autre offre et
  // revient se verrait réappliquer un code qu'il croyait abandonné.
  // Il ne concerne que les ABONNEMENTS, pas la lettre à l'unité : le
  // champ d'inscription était affiché pour une formule, pas pour elle.
  const [signupPromo] = useState(takeSignupPromo);
  const [modalData, setModalData] = useState(null);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(0);
  // L'accordéon mobile s'ouvre sur la formule mise en avant.
  const [planOpen, setPlanOpen] = useState(HIGHLIGHT_COL);
  const [mobile, setMobile] = useState(false);

  // Bascule desktop/mobile de la matrice — seuil de la référence (820px).
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 820);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Analytics — même signal de montage que l'ancienne page.
  useEffect(() => { track('plans_viewed', {}); }, []);

  /* Le mot d'après-annulation. Une fois lu, le paramètre quitte la barre
     d'adresse : un rechargement, un partage ou un retour arrière ne doit
     pas rejouer l'annonce. Les autres paramètres (`tier`, `cycle`)
     survivent — ils portent le choix fait à l'inscription. */
  useEffect(() => {
    if (searchParams.get('subscription') !== 'cancel') return;
    toast({ title: CANCEL_NOTICE[lang] || CANCEL_NOTICE.fr });
    const url = new URL(window.location.href);
    url.searchParams.delete('subscription');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reprise du choix fait ailleurs : `?tier=` ouvre directement l'étape de
  // confirmation, dans le cycle de `?cycle=`. Le client qui a coché
  // « Pro / Annuel » à l'inscription atterrit donc sur le récapitulatif et
  // la case VI.53, pas sur la grille qu'il vient de parcourir.
  //
  // UNE SEULE FOIS, au montage : sans ça, fermer la modale la rouvrirait
  // aussitôt, l'URL n'ayant pas changé. Le plan gratuit n'ouvre rien —
  // il n'y a pas de paiement à confirmer.
  useEffect(() => {
    if (!urlTier) return;
    const tier = normalizeTier(urlTier);
    if (tier === 'free') return;
    setConfirmTier(tier);
    track('checkout_confirm_opened', {
      tier,
      billing_cycle: normalizeBillingCycle(urlCycle),
      source: 'url',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Checkout Stripe — repris à l'identique de PricingPlansPage ──────
  //
  // UN SEUL APPELANT : le bouton de l'étape de confirmation. La carte, elle,
  // n'ouvre plus que cette étape — un clic sur « Choisir » ne débite rien.
  const handleSubscribe = useCallback(async (tier) => {
    setBusy(tier);
    track('checkout_opened', { tier, billing_cycle: cycle });
    try {
      const { data } = await axios.post(
        `${API}/subscriptions/checkout`,
        // `consent_execution` n'est pas décoratif : le backend refuse la
        // session sans lui (400 consent_execution_required). Le bouton
        // qui déclenche cet appel est désactivé tant que la case de
        // l'étape de confirmation n'est pas cochée, donc on ne peut
        // arriver ici qu'avec `true`.
        // `billing_cycle` part explicitement : le backend refuse toute
        // valeur inconnue plutôt que de retomber sur le mensuel.
        // `promo_code` : le TEXTE du code, revérifié côté serveur avant
        // la session. L'aperçu de prix affiché n'a rien accordé.
        {
          tier,
          billing_cycle: cycle,
          consent_execution: true,
          ...(subPromo?.code ? { promo_code: subPromo.code } : {}),
        },
        { withCredentials: true },
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 401) {
        // Le cycle voyage avec la formule : l'inscription le pré-coche,
        // et le retour sur /plans le repose. Un client qui avait choisi
        // l'annuel ne doit pas se réveiller en mensuel après un détour
        // par la création de compte.
        window.location.href = `/signup?next=/plans&tier=${tier}&cycle=${cycle}`;
        return;
      }
      // Toute erreur referme l'étape de confirmation : le message (modale
      // « bientôt disponible » ou alerte) doit se lire sans un écran de
      // paiement par-dessus.
      setConfirmTier(null);
      const comingSoon = extractCheckoutNotConfigured(e);
      if (comingSoon) {
        setModalData(comingSoon);
        return;
      }
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail && detail.message) || t('checkoutError');
      alert(msg);
    } finally {
      setBusy(null);
    }
  }, [t, cycle, subPromo]);

  // « Choisir [formule] » — ouvre l'étape de confirmation. Le départ vers
  // Stripe est dans `handleSubscribe`, déclenché par le bouton de l'étape.
  const openConfirm = useCallback((tier) => {
    track('checkout_confirm_opened', { tier, billing_cycle: cycle });
    setConfirmTier(tier);
  }, [cycle]);

  // Lettre à l'unité — paiement UNIQUE, jamais un abonnement. Un
  // visiteur non connecté passe d'abord par l'inscription : le crédit se
  // pose sur un compte, il faut donc qu'il en ait un.
  const openOneshot = useCallback(() => {
    track('letter_oneshot_opened', {});
    if (!user) {
      navigate('/signup?next=/plans&product=letter_oneshot');
      return;
    }
    setOneshotConsent(false);
    setOneshotError(null);
    setOneshotPromo(null);
    setOneshotOpen(true);
  }, [user, navigate]);

  const payOneshot = useCallback(async () => {
    setOneshotBusy(true);
    setOneshotError(null);
    try {
      const { data } = await axios.post(
        `${API}/letters/oneshot-checkout`,
        {
          consent_execution: true,
          return_path: '/dashboard',
          ...(oneshotPromo?.code ? { promo_code: oneshotPromo.code } : {}),
        },
        { withCredentials: true },
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 401) {
        navigate('/signup?next=/plans&product=letter_oneshot');
        return;
      }
      setOneshotError(t('humans.letterModalError'));
    } finally {
      setOneshotBusy(false);
    }
  }, [navigate, t, oneshotPromo]);

  const startRoute = useStartRoute();

  // CTA gratuits : /dashboard si connecté, sinon LA PORTE D'ENTRÉE DU
  // SITE. Ce bouton est le seul de /plans qui ne vend rien — il ouvre le
  // parcours, et le parcours d'entrée est celui que `ENABLE_ANON_INTAKE`
  // désigne (cf. hooks/useStartRoute.js), pas `/signup` en dur. Les deux
  // autres départs de cette page continuent d'exiger un compte, et c'est
  // voulu : un abonnement comme un crédit lettre se posent SUR un compte.
  const goFree = useCallback(() => {
    navigate(user ? '/dashboard' : startRoute);
  }, [navigate, user, startRoute]);

  const seoMeta = getPageMetadata('plans', lang);

  // ── Récapitulatif de l'étape de confirmation ────────────────────────
  // Exactement les mêmes montants que la carte : ils sont relus dans
  // `CARDS` / `CYCLE_PRICING`, jamais recalculés. Ce que le client voit
  // avant de payer doit être, au caractère près, ce qu'il a cliqué.
  const confirmCard = confirmTier ? CARDS.find((c) => c.tier === confirmTier) : null;
  const confirmAnnual = confirmCard && cycle === 'annual' && CYCLE_PRICING[confirmCard.tier];
  const confirmDetails = confirmCard
    ? (confirmAnnual
      ? [
        t('cards.cycleAnnual'),
        t('cards.billedAnnually').replace('{A}', confirmAnnual.annual),
        t('cards.commitment'),
      ]
      : [t('cards.cycleMonthly'), t('cards.noCommitment')])
    : [];

  // La matrice vient du dictionnaire : ses libellés ET ses valeurs sont du
  // texte (« 3/mois », « Illimité* »), pas seulement des glyphes.
  const matrix = t('matrix.rows');

  // Lignes de la matrice desktop, avec l'alternance de fonds et les
  // pastilles de groupe (équivalent du `matrix` de `renderVals`).
  let groupIndex = -1;
  let rowIndex = 0;
  const matrixRows = matrix.map((r, i) => {
    if (r.group) {
      groupIndex += 1;
      rowIndex = 0;
      const hue = GHUES[groupIndex % 4];
      return { key: `g${i}`, isGroup: true, label: r.group, hue };
    }
    rowIndex += 1;
    return {
      key: `r${i}`,
      isGroup: false,
      isSoon: !!r.soon,
      label: r.label,
      values: r.v,
      zebra: rowIndex % 2 === 0 ? '#fbfbfa' : '#fff',
    };
  });

  // Accordéon mobile : un panneau par plan, ne listant que ce que le plan
  // inclut (les « — » sont omis, comme dans la référence).
  const plansAcc = MATRIX_PLANS.map((p) => {
    const items = [];
    let gi = -1;
    let pending = null;
    matrix.forEach((r, i) => {
      if (r.group) {
        gi += 1;
        pending = { key: `g${i}`, isGroup: true, label: r.group, hue: GHUES[gi % 4] };
        return;
      }
      const v = r.soon ? t('matrix.soon') : r.v[p.idx];
      if (v === '·') return;
      if (pending) { items.push(pending); pending = null; }
      items.push({ key: `r${i}`, isGroup: false, label: r.label, soon: !!r.soon, val: v });
    });
    return { ...p, items };
  });

  return (
    <div className="credits-ui pv2-root" lang={lang}>
      <PageHead metadata={seoMeta} />
      <QuotaModal data={modalData} onClose={() => setModalData(null)} />
      <SiteHeaderV2 />

      <main>
        {/* ── EN-TÊTE ─────────────────────────────────────────────── */}
        <section
          className="archer-hero-section"
          style={{
            position: 'relative',
            // Blanc sous le halo commun (hero.css) — le dégradé linéaire de
            // la maquette (#f6f8fd → #fbfcfe) reste écarté : c'est le même
            // halo radial que Teams qui donne le fond doux, pas un dégradé
            // propre à cette page. 150px réservaient la hauteur de
            // l'ancienne barre fixe ; le header unique est dans le flux,
            // reste l'espace visuel, 84px.
            background: '#ffffff',
            // Le filet marque la césure vers la grille crème (#f4f4f1) de
            // la section suivante : deux fonds différents, une bordure qui
            // délimite vraiment quelque chose.
            borderBottom: '1px solid #eef2fb',
            paddingLeft: 40,
            paddingRight: 40,
            overflow: 'hidden',
          }}
        >
          {/* Conteneur de 1160px, comme la grille des cartes en dessous.
              À l'intérieur, la rangée du design : mascotte 210px et bloc
              de texte côte à côte, centrés ensemble dans 920px, titre
              aligné à GAUCHE (géométrie dans PlansV2.css, copiée valeur
              par valeur de l'export). */}
          {/* Le fond doux commun à tous les heros ; `--wide` parce que le
              hero est en deux colonnes (hibou + texte) : le halo enveloppe
              les deux. Le conteneur qui suit est `position: relative`. */}
          <div aria-hidden="true" className="archer-hero-halo archer-hero-halo--wide" />
          <div style={{ maxWidth: 1160, margin: '0 auto', position: 'relative' }}>
            <div className="pv2-hero-row">
              {/* Purement décorative : aucune information qu'un lecteur
                  d'écran doive annoncer. Statique — la mascotte ne bouge
                  sur aucune page. */}
              {HERO_MASCOT_SRC && (
                <img
                  className="pv2-hero-mascot-img"
                  src={HERO_MASCOT_SRC}
                  alt=""
                  aria-hidden="true"
                  width={HERO_MASCOT_W}
                  height={HERO_MASCOT_H}
                  loading="eager"
                  decoding="async"
                  draggable="false"
                />
              )}
              <div className="pv2-hero-copy">
                <div
                  style={{
                    fontSize: 13, fontWeight: 800, letterSpacing: '3px',
                    textTransform: 'uppercase', color: '#1a56db', marginBottom: 16,
                  }}
                >
                  {t('hero.kicker')}
                </div>
                <h1 className="archer-hero-title">
                  {t('hero.h1a')}
                  <br />
                  <span className="archer-hero-accent">{t('hero.h1b')}</span>
                </h1>
                {/* Aucune largeur de ligne imposée : le design n'en pose
                    pas. C'est la rangée de 920px et la mascotte de 210px
                    qui bornent la colonne de texte. */}
                <p className="archer-hero-subtitle">
                  {t('hero.sub')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── LES 4 CARTES + MATRICE ──────────────────────────────── */}
        <section style={{ background: '#f4f4f1', padding: '44px 40px 36px' }}>
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            {/* ── Cycle de facturation. Mensuel d'abord, toujours : le
                   sélectionner change un engagement, pas un affichage. ── */}
            <div
              role="radiogroup"
              aria-label={t('cards.cycleMonthly')}
              data-testid="plans-cycle-toggle"
              style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: 6, marginBottom: 26,
              }}
            >
              <div
                style={{
                  display: 'inline-flex', gap: 4, padding: 4,
                  background: '#ffffff', border: '1px solid #ececea',
                  borderRadius: 999, boxShadow: '0 2px 10px rgba(10,10,15,.04)',
                }}
              >
                {[
                  { key: 'monthly', label: t('cards.cycleMonthly') },
                  { key: 'annual', label: t('cards.cycleAnnual') },
                ].map((opt) => {
                  const on = cycle === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      data-testid={`plans-cycle-${opt.key}`}
                      onClick={() => {
                        setCycle(opt.key);
                        // Changer de cycle change le contrat : une étape de
                        // confirmation ouverte pour un mensuel ne peut pas
                        // se transformer en engagement annuel sous les yeux
                        // du client. Elle se referme.
                        setConfirmTier(null);
                        track('plans_cycle_switched', { billing_cycle: opt.key });
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                        padding: '9px 18px', borderRadius: 999, border: 'none',
                        background: on ? '#1a56db' : 'transparent',
                        color: on ? '#ffffff' : '#4b5563',
                        fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
                        cursor: 'pointer', transition: 'all .15s',
                      }}
                    >
                      {opt.label}
                      {opt.key === 'annual' && (
                        <span
                          style={{
                            fontSize: 11, fontWeight: 700, padding: '2px 8px',
                            borderRadius: 999, whiteSpace: 'nowrap',
                            background: on ? 'rgba(255,255,255,.2)' : '#dcfce7',
                            color: on ? '#ffffff' : '#15803d',
                          }}
                        >
                          {t('cards.cycleBadge')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pv2-cards" data-testid="plans-grid">
              {CARDS.map((c) => {
                const isPro = !!c.highlight;
                const isFree = c.tier === 'free';
                const copy = t(`cards.${c.tier}`);
                // En annuel, la carte met en avant l'ÉQUIVALENT MENSUEL
                // (34€ / 69€) et écrit dessous comment il est facturé
                // (« facturé annuellement · engagement 12 mois »). Le prix
                // en gros reste donc « /mois » dans les deux cycles :
                // c'est la seule façon de comparer les deux d'un coup
                // d'œil, et la ligne du dessous empêche ce gros chiffre de
                // laisser croire qu'on paie au mois sans s'engager. Le
                // plan gratuit n'a pas de cycle : 0 €.
                const annual = !isFree && cycle === 'annual' && CYCLE_PRICING[c.tier];
                const shownPrice = annual ? annual.perMonth : c.price;
                const shownSuffix = !c.suffix
                  ? null
                  : (PRICE_SUFFIX[lang] || PRICE_SUFFIX.fr);
                return (
                  <div
                    key={c.tier}
                    style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
                    data-testid={`plan-card-${c.tier}`}
                  >
                    <div style={{ height: 26 }} />
                    <div
                      className={isPro ? 'pv2-card pv2-card-pro' : 'pv2-card'}
                      style={
                        isPro
                          ? {
                            flex: 1, display: 'flex', flexDirection: 'column', position: 'relative',
                            background: 'linear-gradient(165deg,#1a56db 0%,#1e40af 100%)',
                            border: 'none', borderRadius: 16, padding: '21px 19px',
                            boxShadow: '0 6px 20px rgba(26,86,219,.16)', transition: 'all .18s ease',
                          }
                          : {
                            flex: 1, display: 'flex', flexDirection: 'column', background: '#fff',
                            border: '1px solid #ececea', borderRadius: 16, padding: '20px 18px',
                            boxShadow: '0 2px 12px rgba(10,10,15,.03)', transition: 'all .18s ease',
                          }
                      }
                    >
                      {isPro && (
                        <span
                          style={{
                            position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)',
                            display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff',
                            color: '#1a56db', fontSize: 13, fontWeight: 700, letterSpacing: '.03em',
                            padding: '7px 18px', borderRadius: 999,
                            boxShadow: '0 8px 20px rgba(10,20,60,.35)', whiteSpace: 'nowrap',
                          }}
                        >
                          <Bolt />
                          {t('cards.recommended')}
                        </span>
                      )}
                      <div
                        style={{
                          width: 34, height: 4, borderRadius: 99,
                          background: isPro ? 'rgba(255,255,255,.9)' : c.bar, marginBottom: 12,
                        }}
                      />
                      <div
                        style={{
                          fontSize: 11, fontWeight: 800, letterSpacing: '.14em',
                          color: isPro ? 'rgba(255,255,255,.8)' : c.nameColor,
                        }}
                      >
                        {c.name}
                      </div>
                      <div
                        style={{
                          fontStyle: 'italic', fontSize: 'clamp(12px,1.08vw,15.5px)', fontWeight: 500,
                          letterSpacing: '-.005em', color: isPro ? 'rgba(255,255,255,.85)' : '#4b5563',
                          margin: '6px 0 14px', height: 23, whiteSpace: 'nowrap', overflow: 'hidden',
                        }}
                      >
                        {copy.identity}
                      </div>
                      <div
                        style={{
                          display: 'flex', alignItems: 'baseline', gap: 4,
                          marginBottom: annual ? 4 : 14, height: annual ? 48 : 56,
                        }}
                        data-testid={`plan-price-${c.tier}`}
                      >
                        <span
                          style={{
                            fontSize: isPro ? 46 : 42, lineHeight: 1, fontWeight: 700,
                            letterSpacing: '-.045em', color: isPro ? '#fff' : '#0a0a0f',
                          }}
                        >
                          {shownPrice}
                        </span>
                        {shownSuffix && (
                          <span
                            style={{
                              fontSize: 13, fontWeight: 500,
                              color: isPro ? 'rgba(255,255,255,.65)' : '#9ca3af',
                            }}
                          >
                            {shownSuffix}
                          </span>
                        )}
                      </div>
                      {/* Ce que le gros chiffre ne dit pas — et RIEN
                          d'autre. En annuel : la facture part en une fois
                          et engage douze mois, la seule chose qu'un prix
                          mensuel affiché en gros passe sous silence. En
                          mensuel : rien, parce qu'il n'y a rien à ajouter
                          à « 39€ /mois » — la ligne portait « sans
                          engagement », qui est vrai, se dit déjà dans la
                          FAQ et le pied de page, et n'a pas d'équivalent
                          sous les cartes de l'inscription : deux écrans
                          qui vendent la même formule ne s'écrivent pas
                          différemment. La hauteur reste réservée dans les
                          deux cas, sinon la grille sauterait d'un cycle à
                          l'autre.

                          Le MONTANT débité (408€) a quitté la carte avec
                          la pastille : il se lit à l'étape de
                          confirmation, juste avant de payer, là où il
                          engage (cf. `confirmAnnual`). */}
                      {!isFree && (
                        <div
                          style={{
                            minHeight: 22, marginBottom: 10, display: 'flex',
                            alignItems: 'center', gap: 8, flexWrap: 'wrap',
                          }}
                        >
                          {annual && (
                            <span
                              data-testid={`plan-billed-${c.tier}`}
                              style={{
                                fontSize: 12.5, fontWeight: 500,
                                color: isPro ? 'rgba(255,255,255,.75)' : '#6b7280',
                              }}
                            >
                              {t('cards.annualNote')}
                            </span>
                          )}
                          {annual && (
                            <span
                              data-testid={`plan-saving-${c.tier}`}
                              style={{
                                fontSize: 11, fontWeight: 700, padding: '2px 8px',
                                borderRadius: 999, whiteSpace: 'nowrap',
                                background: isPro ? 'rgba(255,255,255,.2)' : '#dcfce7',
                                color: isPro ? '#ffffff' : '#15803d',
                              }}
                            >
                              {t('cards.annualSaving')
                                .replace('{P}', String(annual.savingPct))
                                .replace('{S}', String(annual.savingEur))}
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 11, fontWeight: 600, letterSpacing: '.04em', marginBottom: 10,
                          ...(copy.inheritance
                            ? { color: isPro ? 'rgba(255,255,255,.6)' : '#9ca3af' }
                            : { visibility: 'hidden' }),
                        }}
                      >
                        {copy.inheritance || '·'}
                      </div>
                      <div
                        style={{
                          display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 18,
                        }}
                      >
                        {copy.features.map((f, i) => (
                          <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            style={{
                              display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13,
                              lineHeight: 1.5, color: isPro ? 'rgba(255,255,255,.92)' : '#374151',
                            }}
                          >
                            <span
                              style={{
                                flex: 'none', width: 18, height: 18, borderRadius: '50%',
                                background: '#dcfce7', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', marginTop: 1,
                              }}
                            >
                              <Check />
                            </span>
                            <span>{f}</span>
                          </div>
                        ))}
                      </div>
                      {/* La carte s'arrête ici : elle décrit et elle
                          propose. Le consentement à l'exécution immédiate
                          (art. VI.53 CDE) se donne dans l'étape de
                          confirmation, au moment de payer — pas dans une
                          vitrine, et pas trois fois sur le même écran.
                          `marginTop: auto` replaque le bouton en bas de
                          carte, quelle que soit la hauteur des puces. */}
                      <button
                        type="button"
                        className={isPro ? 'pv2-btn-white' : 'pv2-btn-outline'}
                        data-testid={isFree ? 'plan-cta-free' : `plan-subscribe-${c.tier}`}
                        disabled={busy === c.tier}
                        onClick={() => (isFree ? goFree() : openConfirm(c.tier))}
                        style={
                          isPro
                            ? {
                              marginTop: 'auto', width: '100%', padding: '11px 14px', border: 'none',
                              background: '#fff', color: '#1a56db', borderRadius: 10, fontSize: 13.5,
                              fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                              transition: 'all .15s', boxShadow: '0 6px 18px rgba(10,20,60,.3)',
                            }
                            : {
                              marginTop: 'auto', width: '100%', padding: '10px 14px',
                              border: '1px solid #e5e7eb', background: '#fff', color: '#0a0a0f',
                              borderRadius: 10, fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
                              cursor: 'pointer', transition: 'all .15s',
                            }
                        }
                      >
                        {busy === c.tier ? t('cards.redirecting') : copy.cta}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Ce qui se débloque À LA DEMANDE au-delà des unités du
                plan. Elle est ici, sous les cartes, et pas seulement au
                fond de la FAQ : un prix qu'il faut chercher n'est pas un
                prix annoncé. Le supplément « envoi recommandé » y figure
                — le recommandé n'est inclus dans aucun abonnement. */}
            <div
              data-testid="plans-overage-line"
              style={{
                maxWidth: 860, margin: '20px auto 0', textAlign: 'center',
                fontSize: 12.5, fontWeight: 400, lineHeight: 1.65, color: '#6b7280',
              }}
            >
              {withPrices(t('cards.overageLine'))}
            </div>

            {/* ── MATRICE ─────────────────────────────────────────── */}
            <div style={{ padding: '10px 0 0' }}>
              <div style={{ maxWidth: 1160, margin: '0 auto' }}>
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="pv2-matrix-btn"
                    data-testid="plans-matrix-toggle"
                    aria-expanded={matrixOpen}
                    onClick={() => setMatrixOpen((o) => !o)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 9, padding: '13px 26px',
                      background: '#fff', border: '2px solid #1a56db', borderRadius: 11,
                      fontSize: 14.5, fontWeight: 600, fontFamily: 'inherit', color: '#1a56db',
                      cursor: 'pointer', transition: 'all .15s',
                      boxShadow: '0 4px 14px rgba(26,86,219,.15)',
                    }}
                  >
                    {matrixOpen ? t('matrix.hide') : t('matrix.show')}
                    <Chevron
                      stroke="currentColor"
                      style={{
                        display: 'flex', transition: 'transform .2s',
                        transform: `rotate(${matrixOpen ? 180 : 0}deg)`,
                      }}
                    />
                  </button>
                </div>

                {matrixOpen && (
                  <>
                    {!mobile && (
                      <div
                        className="pv2-fadeup"
                        data-testid="plans-matrix"
                        style={{
                          marginTop: 20, background: '#fff', border: '1px solid #ececea',
                          borderRadius: 16,
                        }}
                      >
                        {/* Entête collant — reste visible au scroll, sous le
                            header public (sticky, ~65px). */}
                        <div
                          className="pv2-matrix-head"
                          style={{
                            position: 'sticky', top: SITE_HEADER_HEIGHT, zIndex: 5, ...MATRIX_COLS,
                            background: '#fafaf8', borderBottom: '1px solid #ececea',
                            borderRadius: '16px 16px 0 0',
                            boxShadow: '0 2px 8px rgba(10,10,15,.04)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex', alignItems: 'center', padding: '12px 12px 12px 22px',
                              fontSize: 11, fontWeight: 800, letterSpacing: '.1em', color: '#9ca3af',
                            }}
                          >
                            {t('matrix.featureCol')}
                          </div>
                          {MATRIX_PLANS.map((p) => {
                            const highlighted = p.idx === HIGHLIGHT_COL;
                            return (
                              <div
                                key={p.name}
                                style={{
                                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                                  justifyContent: 'center', gap: highlighted ? 3 : 2, padding: '10px 6px',
                                  ...(highlighted ? { background: HIGHLIGHT_COL_BG } : null),
                                }}
                              >
                                {/* La colonne mise en avant porte la même
                                    pastille que la carte — et SON nom.
                                    Le nom « PRO » était écrit en dur ici,
                                    du temps où la colonne mise en avant
                                    était Pro : la pastille a suivi le
                                    badge sur Starter, l'étiquette non, et
                                    le tableau annonçait deux colonnes PRO
                                    à deux prix. */}
                                {highlighted ? (
                                  <span
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                      background: 'linear-gradient(135deg,#1a56db,#1e40af)',
                                      color: '#fff', fontSize: 10.5, fontWeight: 800,
                                      letterSpacing: '.1em', borderRadius: 999, padding: '3px 11px',
                                      boxShadow: '0 4px 10px rgba(26,86,219,.25)',
                                    }}
                                  >
                                    <Bolt size={9} />
                                    {p.name}
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: 11, fontWeight: 800, letterSpacing: '.1em',
                                      color: p.color,
                                    }}
                                  >
                                    {p.name}
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontSize: 12.5, fontWeight: 700, letterSpacing: '-.01em',
                                    color: highlighted ? '#1a56db' : '#0a0a0f',
                                  }}
                                >
                                  {p.price}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        {matrixRows.map((r) => {
                          if (r.isGroup) {
                            return (
                              <div
                                key={r.key}
                                style={{
                                  ...MATRIX_COLS, borderTop: '1px solid #f4f4f1', background: '#fff',
                                }}
                              >
                                <div style={{ padding: '14px 12px 6px 22px' }}>
                                  <span
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', fontSize: 11.5,
                                      fontWeight: 700, letterSpacing: '.02em', background: r.hue.bg,
                                      color: r.hue.fg, borderRadius: 999, padding: '5px 13px',
                                    }}
                                  >
                                    {r.label}
                                  </span>
                                </div>
                                {MATRIX_PLANS.map((p, ci) => (
                                  <div
                                    key={p.name}
                                    style={ci === HIGHLIGHT_COL ? { background: HIGHLIGHT_COL_BG } : undefined}
                                  />
                                ))}
                              </div>
                            );
                          }
                          if (r.isSoon) {
                            return (
                              <div key={r.key} style={{ ...MATRIX_COLS, background: '#fdfcff' }}>
                                <div
                                  style={{
                                    display: 'flex', alignItems: 'center', padding: '8px 12px 8px 22px',
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 13, fontWeight: 500, color: '#8b80a8', lineHeight: 1.45,
                                    }}
                                  >
                                    {r.label}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    gridColumn: '2 / span 3', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', padding: '8px 6px',
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 10.5, fontWeight: 600, background: '#f4f4f1',
                                      border: '1px solid #e5e7eb', color: '#9ca3af', borderRadius: 999,
                                      padding: '2px 12px', whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {t('matrix.soon')}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={r.key} style={{ ...MATRIX_COLS, background: r.zebra }}>
                              <div
                                style={{
                                  display: 'flex', alignItems: 'center', padding: '8px 12px 8px 22px',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 13, fontWeight: 500, color: '#374151', lineHeight: 1.45,
                                  }}
                                >
                                  {r.label}
                                </span>
                              </div>
                              {r.values.map((v, ci) => (
                                <div
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={ci}
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '8px 6px',
                                    ...(ci === HIGHLIGHT_COL ? { background: HIGHLIGHT_COL_BG } : null),
                                  }}
                                >
                                  <span style={cellStyle(v, ci === HIGHLIGHT_COL)}>{v}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {mobile && (
                      <div
                        className="pv2-fadeup"
                        data-testid="plans-matrix-mobile"
                        style={{
                          marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10,
                        }}
                      >
                        {plansAcc.map((p) => {
                          const open = planOpen === p.idx;
                          const isPro = p.idx === HIGHLIGHT_COL;
                          return (
                            <div
                              key={p.name}
                              style={{
                                background: '#fff', borderRadius: 14,
                                ...(isPro
                                  ? {
                                    border: '2px solid #1a56db',
                                    boxShadow: '0 8px 24px rgba(26,86,219,.12)',
                                  }
                                  : { border: '1px solid #ececea' }),
                              }}
                            >
                              <div
                                role="button"
                                tabIndex={0}
                                aria-expanded={open}
                                onClick={() => setPlanOpen(open ? -1 : p.idx)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPlanOpen(open ? -1 : p.idx);
                                  }
                                }}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
                                  cursor: 'pointer',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 11.5, fontWeight: 800, letterSpacing: '.12em',
                                    color: p.color,
                                  }}
                                >
                                  {p.name}
                                </span>
                                <span
                                  style={{
                                    fontSize: 14, fontWeight: 700, letterSpacing: '-.01em',
                                    color: '#0a0a0f',
                                  }}
                                >
                                  {p.price}
                                </span>
                                <span
                                  style={{
                                    marginLeft: 'auto', display: 'flex', transition: 'transform .2s',
                                    transform: `rotate(${open ? 180 : 0}deg)`,
                                  }}
                                >
                                  <Chevron size={15} width={2.2} />
                                </span>
                              </div>
                              {open && (
                                <div
                                  style={{
                                    padding: '2px 18px 16px', display: 'flex',
                                    flexDirection: 'column', gap: 7,
                                  }}
                                >
                                  {p.items.map((it) => {
                                    if (it.isGroup) {
                                      return (
                                        <div key={it.key} style={{ marginTop: 8 }}>
                                          <span
                                            style={{
                                              display: 'inline-flex', fontSize: 11, fontWeight: 700,
                                              background: it.hue.bg, color: it.hue.fg,
                                              borderRadius: 999, padding: '4px 11px',
                                            }}
                                          >
                                            {it.label}
                                          </span>
                                        </div>
                                      );
                                    }
                                    const v = it.val;
                                    const valStyle = v === '✓'
                                      ? {
                                        flex: 'none', display: 'inline-flex', alignItems: 'center',
                                        justifyContent: 'center', width: 20, height: 20,
                                        borderRadius: '50%', fontSize: 11, fontWeight: 700,
                                        ...(isPro
                                          ? { background: '#1a56db', color: '#fff' }
                                          : { background: '#dcfce7', color: '#16a34a' }),
                                      }
                                      : it.soon
                                        ? {
                                          flex: 'none', fontSize: 10.5, fontWeight: 600,
                                          background: '#f4f4f1', border: '1px solid #e5e7eb',
                                          color: '#9ca3af', borderRadius: 999, padding: '2px 9px',
                                          whiteSpace: 'nowrap',
                                        }
                                        : {
                                          flex: 'none', fontSize: 12.5, fontWeight: 600,
                                          color: isPro ? '#1a56db' : '#0a0a0f',
                                        };
                                    return (
                                      <div
                                        key={it.key}
                                        style={{
                                          display: 'flex', alignItems: 'center',
                                          justifyContent: 'space-between', gap: 12,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 13, lineHeight: 1.45, minWidth: 0,
                                            ...(it.soon
                                              ? { fontWeight: 400, color: '#b0aea8' }
                                              : { fontWeight: 500, color: '#374151' }),
                                          }}
                                        >
                                          {it.label}
                                        </span>
                                        <span style={valStyle}>{v}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div
                      style={{
                        textAlign: 'center', fontSize: 12, fontWeight: 400, color: '#9ca3af',
                        marginTop: 12,
                      }}
                    >
                      {t('matrix.footnote')}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── COMPLÉMENTS HUMAINS (avocat / lettre 39€) ───────────── */}
        <section style={{ padding: '32px 40px 8px', background: '#ffffff' }}>
          <div className="pv2-humans" style={{ maxWidth: 1160, margin: '0 auto' }}>
            {/* La carte avocat porte désormais l'habillage de la carte
                lettre — fond blanc, filet #ececea, même ombre : deux
                compléments de même nature se présentaient jusqu'ici comme
                deux objets différents, l'un en aplat bleu, l'autre en
                carte. */}
            <Link
              to="/attorneys"
              data-testid="plans-attorney-block"
              style={{
                background: '#fff', border: '1px solid #ececea', borderRadius: 16,
                padding: '24px 26px', display: 'flex', gap: 16, alignItems: 'flex-start',
                boxShadow: '0 2px 12px rgba(10,10,15,.03)',
                flexWrap: 'wrap', color: 'inherit', textDecoration: 'none',
              }}
            >
              <div
                style={{
                  flex: 'none', width: 44, height: 44, borderRadius: 12, background: '#eff6ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  alignSelf: 'flex-start',
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden="true">⚖️</span>
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15.5, fontWeight: 600, letterSpacing: '-.015em', color: '#0a0a0f',
                    marginBottom: 4,
                  }}
                >
                  {t('humans.courtTitle')}
                </div>
                {/* Le gras ne porte plus que sur la PROMESSE — « première
                    consultation offerte ». Sa portée (« pour tout le
                    monde, même sans abonnement ») revient en texte
                    normal : tout mettre en gras ne mettait plus rien en
                    avant. */}
                <div style={{ fontSize: 13.5, fontWeight: 400, lineHeight: 1.6, color: '#4b5563' }}>
                  {t('humans.courtBody')}{' '}
                  <span style={{ fontWeight: 700, color: '#1a56db' }}>
                    {t('humans.courtStrong')}
                  </span>
                  {t('humans.courtTail')}
                </div>
                {/* Un `<button>` DANS un `<a>` est un imbriquement interdit
                    (contenu interactif dans un lien) : les navigateurs le
                    délient et le clic devient imprévisible. Le CTA est donc
                    un `<span>` au style identique — la carte entière est
                    déjà le lien, ce bouton n'a jamais eu à naviguer par
                    lui-même. */}
                <span
                  data-testid="plans-attorney-cta"
                  className="pv2-attorney-cta"
                  style={{
                    display: 'inline-block', marginTop: 14, padding: '10px 18px',
                    borderRadius: 10, border: '1px solid #1a56db', background: '#fff',
                    color: '#1a56db', fontSize: 13.5, fontWeight: 600,
                  }}
                >
                  {t('humans.courtCta')}
                </span>
              </div>
            </Link>

            <div
              data-testid="plans-letter-block"
              style={{
                background: '#fff', border: '1px solid #ececea', borderRadius: 16,
                padding: '24px 26px', display: 'flex', gap: 16, alignItems: 'flex-start',
                boxShadow: '0 2px 12px rgba(10,10,15,.03)', color: 'inherit',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  flex: 'none', width: 44, height: 44, borderRadius: 12, background: '#fef3c7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  alignSelf: 'flex-start',
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden="true">✍️</span>
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15.5, fontWeight: 600, letterSpacing: '-.015em', color: '#0a0a0f',
                    marginBottom: 4,
                  }}
                >
                  {t('humans.letterTitle')}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 400, lineHeight: 1.6, color: '#4b5563' }}>
                  {t('humans.letterBodyPre')}{' '}
                  <span style={{ fontWeight: 700, color: '#b45309' }}>{t('humans.letterPrice')}</span>
                  {t('humans.letterBodyPost')}
                </div>
                {/* Le périmètre de la lettre à l'unité (art. 4.3 des CGV —
                    ce qu'elle inclut, ce qu'elle n'inclut pas, le
                    recommandé en supplément) a quitté la carte : la
                    référence ne le porte plus ici. Il n'a PAS disparu du
                    produit — il est dans l'écran de confirmation, juste
                    au-dessus de la case VI.53, donc lu avant tout débit.
                    C'est le seul endroit où il engage. */}
                <button
                  type="button"
                  data-testid="plans-letter-cta"
                  onClick={openOneshot}
                  style={{
                    marginTop: 14, padding: '10px 18px', borderRadius: 10,
                    border: '1px solid #b45309', background: '#fff', color: '#b45309',
                    fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                  }}
                >
                  {t('humans.letterCta')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Abonnements — étape de confirmation avant Stripe ─────────
               Elle porte la case VI.53 pour les trois formules payantes,
               dans les deux cycles. Rien ne part vers Stripe sans elle. */}
        <PaymentConfirmModal
          open={!!confirmCard}
          language={lang}
          busy={busy === confirmTier}
          testId="plan-confirm"
          offerName={confirmCard ? `Archer ${confirmCard.name}` : ''}
          amount={confirmAnnual ? confirmAnnual.annual : (confirmCard ? confirmCard.price : '')}
          amountSuffix={
            confirmAnnual
              ? t('cards.suffixYear')
              : (confirmCard ? (PRICE_SUFFIX[lang] || PRICE_SUFFIX.fr) : null)
          }
          details={confirmDetails}
          promoPlan={confirmTier}
          promoBillingCycle={cycle}
          // Code saisi à l'INSCRIPTION. Il est reposé dans le champ et
          // revalidé, jamais redemandé : quelqu'un qui vient de taper son
          // code deux écrans plus tôt et à qui on redemande le même code
          // se demande à juste titre lequel des deux compte.
          promoInitialCode={signupPromo}
          onPromoChange={setSubPromo}
          onCancel={() => { setSubPromo(null); setConfirmTier(null); }}
          onConfirm={() => handleSubscribe(confirmTier)}
        />

        {/* ── Lettre à l'unité — écran de confirmation ─────────────── */}
        {oneshotOpen && (
          <div
            role="dialog"
            aria-modal="true"
            data-testid="letter-oneshot-modal"
            onClick={(e) => {
              if (e.target === e.currentTarget && !oneshotBusy) setOneshotOpen(false);
            }}
            style={{
              position: 'fixed', inset: 0, zIndex: 1200, padding: 20,
              background: 'rgba(10,10,15,.55)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div
              style={{
                background: '#fff', borderRadius: 18, border: '1px solid #e2e0db',
                width: '100%', maxWidth: 460, padding: '26px 26px 20px',
                maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto',
                boxShadow: '0 30px 60px -20px rgba(15,23,42,.35)',
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, color: '#b45309', letterSpacing: 1, marginBottom: 10 }}>
                {t('humans.letterModalTitle').toUpperCase()}
              </div>
              {/* Le prix barré n'apparaît qu'avec un code validé par le
                  serveur, et affiche le montant que Stripe débitera. */}
              <div
                data-testid="letter-oneshot-price"
                style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.03em', color: '#0a0a0f', marginBottom: 12 }}
              >
                {oneshotPromo && typeof oneshotPromo.final_amount === 'number' ? (
                  <>
                    <s style={{ fontSize: 17, fontWeight: 500, color: '#9ca3af', marginRight: 9 }}>
                      {t('humans.letterModalPrice')}
                    </s>
                    <span style={{ color: '#15803d' }} data-testid="letter-oneshot-price-discounted">
                      {formatCents(oneshotPromo.final_amount, oneshotPromo.currency)}
                    </span>
                  </>
                ) : t('humans.letterModalPrice')}
              </div>
              {/* Art. 4.3 des CGV, mot pour mot dans l'esprit : ce que la
                  lettre à l'unité inclut ET ce qu'elle n'inclut pas. */}
              <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.6, color: '#374151' }}>
                {t('humans.letterScope')}
              </p>
              {/* La lettre à l'unité est une offre éligible comme une
                  autre : c'est l'admin qui décide, code par code. */}
              <PromoCodeField
                plan="letter_oneshot"
                language={lang}
                disabled={oneshotBusy}
                showPrice={false}
                testId="letter-oneshot-promo"
                onApplied={setOneshotPromo}
              />
              <div style={{ height: 12 }} />
              {oneshotError && (
                <div style={{
                  margin: '0 0 12px', padding: '10px 12px', background: '#fef2f2',
                  border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, color: '#991b1b',
                }}>
                  {oneshotError}
                </div>
              )}
              <ConsentExecutionCheckbox
                id="consent-execution-letter-oneshot"
                testId="consent-execution-letter-oneshot"
                checked={oneshotConsent}
                onChange={setOneshotConsent}
                disabled={oneshotBusy}
              />
              <button
                type="button"
                data-testid="letter-oneshot-pay"
                onClick={payOneshot}
                disabled={oneshotBusy || !oneshotConsent}
                style={{
                  width: '100%', padding: '12px 18px', border: 'none', borderRadius: 10,
                  background: '#1a56db', color: '#fff', fontSize: 14, fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: (oneshotBusy || !oneshotConsent) ? 'not-allowed' : 'pointer',
                  opacity: (oneshotBusy || !oneshotConsent) ? 0.5 : 1,
                }}
              >
                {/* Le bouton porte le montant : avec un code appliqué, il
                    doit porter le MONTANT RÉDUIT. « Payer 19 € » sous un
                    prix réduit affiché serait précisément la surprise
                    qu'on s'interdit — et c'est le dernier texte lu avant
                    le clic. */}
                {oneshotBusy
                  ? t('cards.redirecting')
                  : (oneshotPromo && typeof oneshotPromo.final_amount === 'number'
                    ? t('humans.letterModalPayAmount')
                      .replace('{P}', formatCents(oneshotPromo.final_amount, oneshotPromo.currency))
                    : t('humans.letterModalPay'))}
              </button>
              {/* La promesse qui compte pour ce produit : il s'adresse à
                  qui ne veut PAS s'abonner. */}
              <div style={{ marginTop: 10, fontSize: 11.5, color: '#6b7280', textAlign: 'center', lineHeight: 1.55 }}>
                {t('humans.letterModalNote')}
              </div>
              <button
                type="button"
                onClick={() => setOneshotOpen(false)}
                disabled={oneshotBusy}
                style={{
                  marginTop: 8, width: '100%', padding: '9px 12px', background: 'transparent',
                  border: 'none', color: '#6b7280', fontSize: 12.5, fontWeight: 600,
                  fontFamily: 'inherit', cursor: oneshotBusy ? 'not-allowed' : 'pointer',
                }}
              >
                {t('humans.letterModalCancel')}
              </button>
            </div>
          </div>
        )}

        {/* ── CTA FINAL ───────────────────────────────────────────── */}
        <section
          style={{
            background: '#f4f4f1', borderTop: '1px solid #e8e8e4', padding: '72px 40px',
            textAlign: 'center', position: 'relative', overflow: 'hidden',
          }}
        >
          <div style={{ maxWidth: 620, margin: '0 auto', position: 'relative' }}>
            <h2
              style={{
                fontSize: 'clamp(32px,4.2vw,48px)', fontWeight: 600, letterSpacing: '-.035em',
                lineHeight: 1.08, color: '#0a0a0f', margin: 0,
              }}
            >
              {t('final.h1a')}
              <br />
              <span style={{ color: '#1a56db' }}>{t('final.h1b')}</span>
            </h2>
            <div
              className="pv2-cta-row"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20,
                marginTop: 26,
              }}
            >
              {MASCOT_SRC && (
                <picture style={{ display: 'block', flex: 'none' }}>
                  {MASCOT_WEBP && <source srcSet={MASCOT_WEBP} type="image/webp" />}
                  <img
                    src={MASCOT_SRC}
                    alt={t('final.mascotAlt')}
                    style={{ width: 170, height: 130, objectFit: 'contain' }}
                  />
                </picture>
              )}
              <button
                type="button"
                className="pv2-btn-primary"
                data-testid="plans-final-cta"
                onClick={goFree}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 9, padding: '13px 24px',
                  border: 'none', background: '#1a56db', color: '#fff', borderRadius: 11,
                  fontSize: 15.5, fontWeight: 600, letterSpacing: '-.01em', fontFamily: 'inherit',
                  cursor: 'pointer', transition: 'all .15s',
                  boxShadow: '0 4px 16px rgba(26,86,219,.22)',
                }}
              >
                <svg
                  width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t('final.cta')}
              </button>
            </div>
            <div style={{ marginTop: 14, fontSize: 13, fontWeight: 400, color: '#9ca3af' }}>
              {t('final.note')}
            </div>
          </div>
        </section>

        {/* ── FAQ ─────────────────────────────────────────────────── */}
        <section style={{ padding: '64px 40px 24px', background: '#ffffff' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <h2
              style={{
                fontSize: 'clamp(26px,3vw,34px)', fontWeight: 600, letterSpacing: '-.03em',
                lineHeight: 1.1, margin: '0 0 22px', color: '#0a0a0f', textAlign: 'center',
              }}
            >
              {t('faq.title')}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {t('faq.items').map((f, i) => {
                const on = faqOpen === i;
                return (
                  <div
                    key={f.q}
                    className="pv2-faq"
                    role="button"
                    tabIndex={0}
                    aria-expanded={on}
                    data-testid={`plans-faq-${i}`}
                    onClick={() => setFaqOpen(on ? -1 : i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setFaqOpen(on ? -1 : i);
                      }
                    }}
                    style={{
                      background: '#fff', border: `1px solid ${on ? '#dbeafe' : '#ececea'}`,
                      borderRadius: 12, padding: '16px 20px', cursor: 'pointer',
                      transition: 'all .18s ease',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 16,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', color: '#0a0a0f',
                        }}
                      >
                        {f.q}
                      </div>
                      <div
                        style={{
                          flex: 'none', display: 'flex', transition: 'transform .2s',
                          transform: `rotate(${on ? 180 : 0}deg)`,
                        }}
                      >
                        <Chevron size={16} width={2} />
                      </div>
                    </div>
                    {on && (
                      <div
                        style={{
                          fontSize: 14, fontWeight: 400, lineHeight: 1.65, color: '#4b5563',
                          marginTop: 10,
                        }}
                      >
                        {f.a}
                      </div>
                    )}
                    {on && f.prices && (
                      <>
                        <div
                          className="pv2-prices"
                          style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}
                        >
                          {f.prices.map((pr) => (
                            <div
                              key={pr.label}
                              style={{
                                flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column',
                                gap: 2, alignItems: 'center', background: '#fafaf8',
                                border: '1px solid #ececea', borderRadius: 12, padding: '11px 16px',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12.5, fontWeight: 500, color: '#374151',
                                  textAlign: 'center',
                                }}
                              >
                                {pr.label}
                              </span>
                              <span
                                style={{
                                  fontSize: 16, fontWeight: 700, letterSpacing: '-.02em',
                                  color: '#1a56db',
                                }}
                              >
                                {pr.price}
                              </span>
                              <span
                                style={{
                                  fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em',
                                  color: '#9ca3af',
                                }}
                              >
                                {pr.plans}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            fontSize: 12, fontWeight: 400, color: '#9ca3af', marginTop: 10,
                            textAlign: 'center',
                          }}
                        >
                          {f.note}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                textAlign: 'center', fontSize: 11.5, fontWeight: 400, color: '#9ca3af',
                marginTop: 18,
              }}
            >
              {t('faq.footnote')}
            </div>
          </div>
        </section>
      </main>

      <SiteFooterV2 />
    </div>
  );
}
